import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import { useBoards } from "Boards/BoardContext";

import FnrIcon from "UI/FnrIcon";
import { FnrPopover } from "UI/FnrPopover2";

import { Cell } from "./";

NameCell.propTypes = {
  name: PropTypes.string,
  entityID: PropTypes.number,
  entityType: PropTypes.string,
  primaryUserName: PropTypes.string,
  relatedEntity: PropTypes.object,
};

export function NameCell({
  name,
  entityID,
  entityType,
  primaryUserName,
  relatedEntity,
}) {
  const [showPopover, setShowPopover] = useState(false);
  const triggerRef = useRef();
  const [state] = useBoards();
  const boardIsSorted = state.sortInfo?.columnID;
  const isAdditionalName = entityType === "signupAdditionalName";
  const goToProfile = () => window.fnReactBus.loadUserProfile(entityID);

  const renderName = () => {
    const hasName = name?.trim().length > 0;
    return hasName ? name : <NoName>No Name</NoName>;
  };

  return (
    <Wrapper columnID="names">
      {isAdditionalName ? (
        <AdditionalName $shouldIndent={!boardIsSorted}>
          {renderName()}

          <ANIcon ref={triggerRef} onClick={() => setShowPopover(!showPopover)}>
            <FnrIcon fad fa-user-friends />
          </ANIcon>

          <FnrPopover
            buttonRef={triggerRef}
            show={showPopover}
            onShouldClose={() => setShowPopover(false)}
          >
            <PopoverContent>
              {renderName()} is an additional person signed up by{" "}
              <PrimaryUserName
                className={`can_mini_profile can_mini_profile_${relatedEntity?.entityID}`}
                data-userid={relatedEntity?.entityID}
              >
                {primaryUserName}
              </PrimaryUserName>
              .
            </PopoverContent>
          </FnrPopover>
        </AdditionalName>
      ) : (
        <Name
          className={`can_mini_profile can_mini_profile_${entityID}`}
          data-userid={entityID}
          onClick={goToProfile}
        >
          {renderName()}
        </Name>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(Cell)`
  font-weight: 400;
  position: sticky;
  left: 12px;
  z-index: 99;
  flex-grow: 0;
  flex-shrink: 0;
  background: #fff;
  padding-left: 6px;
  width: 170px !important;
  font-size: 16px;

  &::after {
    content: "";
    width: 8px;
    top: 0;
    bottom: 0;
    position: absolute;
    right: -8px;
    background-image: linear-gradient(
      to right,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0)
    );
  }

  &::before {
    content: "";
    width: 20px;
    top: 0;
    bottom: 0;
    position: absolute;
    right: 100%;
    background: #fff;
  }

  @media (max-width: 600px) {
    width: 140px;
    font-size: 13px;
  }
`;

const Name = styled.div`
  max-width: 170px;
  width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 16px;
  cursor: pointer;
  padding-right: 0;
  line-height: 1.2;
`;

const NoName = styled.div`
  color: #98a4a5;
  font-style: italic;
`;

const AdditionalName = styled(Name)`
  padding-left: ${({ $shouldIndent }) => ($shouldIndent ? "6px" : null)};
  display: flex;
  cursor: default;
  font-size: 0.9rem;
  font-style: italic;
`;

const ANIcon = styled.div`
  cursor: pointer;
  font-size: 0.8rem;
  margin-left: 5px;
  opacity: 0.6;
  position: relative;
  top: 2px;
  transition: opacity 150ms, transform 100ms;

  &:hover {
    opacity: 1;
  }

  &:active {
    transform: scale(0.9);
  }
`;

const PopoverContent = styled.div`
  max-width: 200px;
  padding: 8px 10px 10px;
`;

const PrimaryUserName = styled.span`
  text-decoration: underline;
  text-decoration-style: dotted;
  text-decoration-color: rgba(255, 255, 255, 0.5);
  text-decoration-thickness: 1.2px;
  text-underline-offset: 2px;
  cursor: default;
`;
